/**
 * Email domains that say nothing about the company behind an address — the ONE
 * list shared by every surface: admin front (company logo, newsletter CSV
 * import) and Edge Functions (gmail-sync matching, company resolution).
 * Pure module (no Deno / DOM API) so Vite and Deno import the same file.
 *
 * Why one list: four divergent copies (15 to 27 domains) let `neuf.fr` or
 * `msn.com` count as a company on one surface and not on another, and the
 * newsletter CSV import attached every `@gmail.com` address — the team's
 * personal ones included — to the account whose website was `gmail.com`
 * (Bancarel incident, 15/02/2026).
 *
 * The company's own domains are not listed here: they are the record's
 * (`organizations.internal_domains`, read through `organization_profile()` at the
 * Edge and `useOrganization()` in the front) and every function below that needs
 * them takes them as a parameter (PLAN-GENERICITE § 4 quinquies, R1).
 *
 * SQL mirrors: `is_non_company_email_domain` = FREE ∪ the record's domains
 * (`is_internal_domain`), used by the newsletter CSV import matching;
 * `src/lib/newsletter/emailDomainsSqlMirror.test.ts` fails when the free lists
 * differ: change this list, then a new migration redefining the SQL function.
 * `isInternalDomain` / `isInternalEmail` are the twins of SQL `is_internal_domain`
 * / `is_internal_email`, held to the same answers by a shared corpus
 * (`email-domains.test.ts`, proved against production on 07/10/2026).
 */

/** Consumer / free mail providers: their domain is never a company's brand. */
export const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  "gmail.com", "googlemail.com",
  "hotmail.com", "hotmail.fr", "outlook.com", "outlook.fr", "live.com", "live.fr", "msn.com",
  "yahoo.com", "yahoo.fr", "ymail.com", "aol.com",
  "icloud.com", "me.com", "mac.com",
  "orange.fr", "wanadoo.fr", "free.fr", "sfr.fr", "neuf.fr", "bbox.fr", "numericable.fr",
  "laposte.net", "gmx.com", "gmx.fr", "protonmail.com", "proton.me",
]);

/**
 * Relay domains: the address is built by a platform on behalf of SOMEONE ELSE
 * (Mailinblack anti-spam challenges `cars_faure@invitations.mailinblack.com`,
 * Intercom notifications). Their domain says nothing about any customer; before
 * 17/09/2026 every such mail landed on one ghost account named after the relay.
 */
export const RELAY_EMAIL_DOMAIN_SUFFIXES: readonly string[] = [
  "mailinblack.com",
  "intercom-mail.com",
];

/**
 * Local parts of addresses no human writes from: notifications, reports, bots.
 * Measured 17/09/2026: `gemini-notes@google.com` (134 meeting notes),
 * `calendar-notification@google.com`, `noreply-dmarc-support@google.com`,
 * `dmarcreport@microsoft.com` had been matched to an account by domain and to a
 * "contact" named after the robot.
 */
const AUTOMATED_LOCAL_PARTS: readonly RegExp[] = [
  /^no-?reply\b/, // noreply@, no-reply-eventsatgoogle@, noreply-dmarc-support@
  /[-_.]no-?reply$/, // sc-noreply@, cloudplatform-noreply@
  /^notifications?$/,
  /[-_.]notifications?$/, // calendar-notification@
  /^gemini-notes$/,
  /dmarc/, // dmarcreport@, dmarc@
  /^mailer-daemon$/,
  /^postmaster$/,
  /^bounces?(\+|$)/,
];

function hasSuffixDomain(domain: string, suffix: string): boolean {
  return domain === suffix || domain.endsWith(`.${suffix}`);
}

/**
 * True when an address is a machine or a relay, never a person nor a customer's
 * domain: such an address must not attach a mail to a contact or an account.
 */
export function isAutomatedEmailAddress(email: string | null | undefined): boolean {
  const normalized = (email ?? "").trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at <= 0) return false;
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  if (RELAY_EMAIL_DOMAIN_SUFFIXES.some((suffix) => hasSuffixDomain(domain, suffix))) return true;
  return AUTOMATED_LOCAL_PARTS.some((pattern) => pattern.test(local));
}

/** A consumer mail provider (`FREE_EMAIL_DOMAINS`), whatever the case or surrounding blanks. */
export function isFreeEmailDomain(domain: string | null | undefined): boolean {
  return FREE_EMAIL_DOMAINS.has((domain ?? "").trim().toLowerCase());
}

/** Twin of SQL `is_domain_name`: a lower-case DNS name of two labels or more. */
const DOMAIN_NAME = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/;

/**
 * Twin of SQL `is_internal_domain`: the domain, trimmed and lower-cased, is one of
 * the company's domains or a subdomain of one. `internalDomains` is the record's
 * `internal_domains` (lower-case DNS names, checked by the database).
 */
export function isInternalDomain(domain: string | null | undefined, internalDomains: readonly string[]): boolean {
  const normalized = (domain ?? "").trim().toLowerCase();
  return internalDomains.some((internal) => normalized === internal || normalized.endsWith(`.${internal}`));
}

/**
 * The company's own addresses, as the base says them (PLAN-MULTI-TENANT DM22, lot AG): the record's domains, a
 * shortcut when the company has one, and the team's exact addresses. A company without a domain of its own (its
 * people at @gmail.com, @orange.fr) is known by its addresses alone; a client at the same public service stays a client.
 */
export interface CompanyAddresses {
  /** The record's `internal_domains` (lower-case DNS names, checked by the database). */
  readonly domains: readonly string[];
  /** SQL `team_addresses()`: each member's sign-in, the mailboxes they connected, the shared addresses; lower-case. */
  readonly addresses: readonly string[];
}

/**
 * Twin of SQL `is_internal_email`: the address belongs to the company, by its domain or because it is one of the
 * team's, exactly. Its domain is what follows the LAST @, a valid DNS name; the local part before it is not blank and
 * has no space. A display name (« Alice <a@b.fr> ») is not an address.
 */
export function isInternalEmail(email: string | null | undefined, company: CompanyAddresses): boolean {
  const normalized = (email ?? "").trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at <= 0) return false;
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  return !/\s/.test(local) && domain.length <= 253 && DOMAIN_NAME.test(domain) &&
    (isInternalDomain(domain, company.domains) || company.addresses.includes(normalized));
}

/**
 * Twin of SQL `is_team_address`: one of the team's exact addresses (`company.addresses`, from `team_addresses()`),
 * case and surrounding blanks aside; never a domain. The triage marks such a sender (lot AG).
 */
export function isTeamAddress(email: string | null | undefined, company: CompanyAddresses): boolean {
  const normalized = (email ?? "").trim().toLowerCase();
  return normalized !== "" && company.addresses.includes(normalized);
}

/** What an email domain tells about the company behind an address. */
export type EmailDomainKind = "free" | "internal" | "company";

/** `internalDomains`: the record's `internal_domains`. */
export function classifyEmailDomain(
  domain: string | null | undefined,
  internalDomains: readonly string[],
): EmailDomainKind {
  if (isFreeEmailDomain(domain)) return "free";
  if (isInternalDomain(domain, internalDomains)) return "internal";
  return "company";
}
